import React from 'react'
import { observer } from 'mobx-react'
import Icon from 'components/Icon'
import { Card, CardContent } from 'components/Card'
import cx from 'classnames'
import styles from './FirmDashboardQuickSelectFilter.sass'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import FirmDashboardQuickSelectFilterViewModel from './FirmDashboardQuickSelectFilterViewModel'

interface FirmDashboardQuickSelectFilterProps<
  TFilters extends Record<string, unknown>,
> {
  filter: SelectFilter<keyof TFilters, JobStatusFilterOption>
  vm: FirmDashboardQuickSelectFilterViewModel
}

export default observer(function FirmDashboardQuickSelectFilter<
  TFilters extends Record<string, unknown>,
>({ filter, vm }: FirmDashboardQuickSelectFilterProps<TFilters>) {
  if (!filter) {
    return
  }

  return (
    <div className={styles.root}>
      <h1>Quick select</h1>
      <div className={styles.cardsContainer}>
        <FirmDashboardQuickSelectFilterCard
          title={'Under construction'}
          option={JobStatusFilterOption.Onboarding}
          filter={filter}
          vm={vm}
        />
        <FirmDashboardQuickSelectFilterCard
          title={'In progress'}
          option={JobStatusFilterOption.InProgress}
          filter={filter}
          vm={vm}
        />
        <FirmDashboardQuickSelectFilterCard
          title={'Completed'}
          option={JobStatusFilterOption.Closed}
          filter={filter}
          vm={vm}
        />
      </div>
    </div>
  )
})

interface FirmDashboardQuickSelectFilterCardProps<
  TFilters extends Record<string, unknown>,
> {
  title: string
  option: JobStatusFilterOption
  filter: SelectFilter<keyof TFilters, JobStatusFilterOption>
  vm: FirmDashboardQuickSelectFilterViewModel
}

const FirmDashboardQuickSelectFilterCard = observer(
  function FirmDashboardQuickSelectFilterCard<
    TFilters extends Record<string, unknown>,
  >({
    title,
    option,
    filter,
    vm,
  }: FirmDashboardQuickSelectFilterCardProps<TFilters>) {
    const isSelected = filter.isSelected(option)
    const applyFilter = isSelected
      ? () => filter.clearValue()
      : () => {
          if (title === 'In progress') {
            const options = (filter.selected = [
              JobStatusFilterOption.InProgress,
              JobStatusFilterOption.OnHold,
              JobStatusFilterOption.Idle,
            ])
            filter.change(options)
          } else {
            filter.change([option])
          }
        }

    return (
      <button
        className={cx(
          styles.filterCardContainer,
          filter.value[0] === option && styles.active,
        )}
        onClick={applyFilter}
      >
        <Card borderless>
          <CardContent compact>
            <div
              className={cx(
                styles.filterCardContent,
                styles[
                  `filterCardContentVariant-${vm.mapJobStatusOptionToString(
                    option,
                  )}`
                ],
              )}
            >
              <div
                className={cx(
                  styles.filterCardIcon,
                  styles[
                    `filterCardIconVariant-${vm.mapJobStatusOptionToString(
                      option,
                    )}`
                  ],
                )}
              >
                {(() => {
                  switch (option) {
                    case JobStatusFilterOption.Onboarding:
                      return (
                        <Icon
                          auto
                          size={24}
                          color="var(--red400)"
                          name="pending"
                        />
                      )
                    case JobStatusFilterOption.InProgress:
                      return (
                        <Icon
                          auto
                          size={24}
                          color="var(--blue400)"
                          name="jobs"
                        />
                      )
                    case JobStatusFilterOption.Closed:
                      return (
                        <Icon
                          auto
                          size={24}
                          color="var(--green400)"
                          name="complete-job"
                        />
                      )
                    default:
                      return null
                  }
                })()}
              </div>
              <h2>{title}</h2>
              <h1>{vm.getTotalJobsFilterCount(option)}</h1>
            </div>
          </CardContent>
        </Card>
      </button>
    )
  },
)
